import React, { useState, useEffect } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { parseNumericValue } from '@/services/pmt2Utils';
import { Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow, TableHead, TableHeader } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { LinkIcon, CheckCircle, AlertCircle } from 'lucide-react'; // Keep LinkIcon, add CheckCircle, AlertCircle
import { cn } from "@/lib/utils"; // Import cn utility
import { fetchLatestOldValuesMap, fetchLatestLedgerEntryDate } from '@/services/pmt3OldValuesService';
import { fetchJlsInventoryData, fetchAverageInventoryData } from '@/services/pmt3DashboardDataService'; // Import new service functions

interface InventoryDashboardProps {
  product: PMT2Product | null;
}

type OldValuesMap = Record<string, string>;

// Define interfaces for fetched data structures
interface JlsInventoryData {
    potentialUnits: any;
    preProcessedUnits: any;
    inbound: any;
}

interface AverageInventoryData {
    avgTotalInventory: any;
    avgDaysOfCover: any;
}

const InventoryDashboard: React.FC<InventoryDashboardProps> = ({ product }) => {
  const [oldValuesMap, setOldValuesMap] = useState<OldValuesMap | null>(null);
  const [inventoryThresholdStatusDate, setInventoryThresholdStatusDate] = useState<Timestamp | null>(null);
  const [jlsInventoryData, setJlsInventoryData] = useState<JlsInventoryData | null>(null);
  const [averageInventoryData, setAverageInventoryData] = useState<AverageInventoryData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // --- Formatting & Utility Helpers ---
  const formatInteger = (value: any): string => {
    const num = parseNumericValue(value);
    if (num === null) return '-';
    return Math.round(num).toLocaleString('en-US');
  };

  const formatDecimal = (value: any, decimals = 1): string => {
      const num = parseNumericValue(value);
      if (num === null) return '-';
      return num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatYesNo = (value: any): string => {
      if (value === null || value === undefined || value === '') return '-';
      const str = String(value).toLowerCase();
      if (str === 'yes' || str === 'true' || str === '1') return 'Yes';
      if (str === 'no' || str === 'false' || str === '0') return 'No';
      return '-'; // Default if not recognized
  };

  const formatDefault = (value: any): string => {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  };

  const calculateDaysAgo = (timestamp: Timestamp | null | undefined): number | null => {
      if (!timestamp || !(timestamp instanceof Timestamp)) return null;
      const now = new Date();
      const pastDate = timestamp.toDate();
      // Set both dates to midnight UTC to compare just the date part
      now.setUTCHours(0, 0, 0, 0);
      pastDate.setUTCHours(0, 0, 0, 0);
      const diffTime = now.getTime() - pastDate.getTime();
      if (diffTime < 0) return 0; // Handle future dates if necessary
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
  };
  // --- End Helpers ---

  // Fetch All Data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      // Reset all state
      setOldValuesMap(null);
      setInventoryThresholdStatusDate(null);
      setJlsInventoryData(null);
      setAverageInventoryData(null);

      if (product?.companyName && product?.id) {
        const company = product.companyName;
        const asin = product.id;

        try {
          const results = await Promise.allSettled([
            fetchLatestOldValuesMap(company, asin), // 0
            fetchLatestLedgerEntryDate(company, asin, 'Inventory Threshold Status'), // 1
            fetchJlsInventoryData(company, asin), // 2
            fetchAverageInventoryData(company, asin) // 3
          ]);

          // Process results
          if (results[0].status === 'fulfilled') setOldValuesMap(results[0].value);
          else console.error("Error fetching old values map:", results[0].reason);

          if (results[1].status === 'fulfilled') setInventoryThresholdStatusDate(results[1].value);
          else console.error("Error fetching Inventory Threshold Status date:", results[1].reason);

          if (results[2].status === 'fulfilled') setJlsInventoryData(results[2].value);
          else console.error("Error fetching JLS Inventory data:", results[2].reason);

          if (results[3].status === 'fulfilled') setAverageInventoryData(results[3].value);
          else console.error("Error fetching Average Inventory data:", results[3].reason);

        } catch (error) {
          console.error("Error fetching Inventory dashboard data:", error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [product]);

  // Configuration for dashboard items
  const inventoryItemsConfig = [
    { label: 'DSR', valueKey: 'DSR', oldValueKey: 'DSR', format: 'decimal' },
    { label: 'Total Inventory', valueKey: 'Total Inventory', oldValueKey: 'Total Inventory', format: 'integer', avgKey: 'avgTotalInventory' },
    { label: 'Days of Cover', valueKey: 'Days of Cover', oldValueKey: 'Days of Cover', format: 'integer', avgKey: 'avgDaysOfCover' },
    { label: 'Allow Automatic Pausing from Low Inventory', valueKey: 'Allow Automatic Pausing from Low Inventory', oldValueKey: 'Allow Automatic Pausing from Low Inventory', format: 'yesNo' },
    { label: 'Disable Low Inventory Pausing System While On-Season', valueKey: 'Disable Low Inventory Pausing System While On-Season', oldValueKey: 'Disable Low Inventory Pausing System While On-Season', format: 'yesNo' },
    { label: 'DOC Threshold to Pause Campaigns', valueKey: 'DOC Threshold to Pause Campaigns', oldValueKey: 'DOC Threshold to Pause Campaigns', format: 'default' },
    { label: 'Inventory Threshold Status', valueKey: 'Inventory Threshold Status', oldValueKey: 'Inventory Threshold Status', format: 'default' },
    { label: 'Turn PPC Back on Inventory Count Threshold', valueKey: 'Turn PPC Back on Inventory Count Threshold', oldValueKey: 'Turn PPC Back on Inventory Count Threshold', format: 'default' },
    { label: 'Days Since Paused from Low Inventory', valueKey: 'Inventory Threshold Status', oldValueKey: 'Days Since Paused from Low Inventory', format: 'daysSincePausedLogic' }, // Special logic
    { label: 'JLS Inventory (If Applicable)', valueKey: null, isHeader: true }, // Header row
    { label: 'Potential Units', valueKey: null, sourceKey: 'potentialUnits', format: 'integer', noOldValue: true }, // Flag items with no old value
    { label: 'Pre-Processed Units', valueKey: null, sourceKey: 'preProcessedUnits', format: 'integer', noOldValue: true }, // Flag items with no old value
    { label: 'Inbound', valueKey: null, sourceKey: 'inbound', format: 'integer', noOldValue: true }, // Flag items with no old value
  ];

  // Helper to format display values
  const formatDisplay = (value: any, formatType?: string): string => {
      if (value === null || value === undefined || value === '') return '-';
      switch (formatType) {
          case 'integer': return formatInteger(value);
          case 'decimal': return formatDecimal(value);
          case 'yesNo': return formatYesNo(value);
          default: return formatDefault(value);
      }
  };

  // Helper for "Days Since Paused..."
  const getDaysSincePausedDisplay = (): string => {
      const status = product?.['Inventory Threshold Status']?.toLowerCase();
      if (status?.includes('pause')) return '-'; // If currently paused, show '-'
      // Otherwise, calculate days since the status was last set (which implies it's no longer paused)
      const days = calculateDaysAgo(inventoryThresholdStatusDate);
      return days !== null ? `${days} days` : '-'; // Show '-' if date is missing
  };


  if (!product) {
       return (
            <Card>
                <CardHeader><CardTitle className="text-2xl font-bold">Inventory</CardTitle></CardHeader>
                <CardContent><p className="text-sm text-gray-500">Select a product to view Inventory details.</p></CardContent>
            </Card>
        );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader className="p-4">
        <CardTitle className="text-2xl font-bold">Inventory</CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead className="w-1/2">Item</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead className="text-right">Old Value</TableHead>
                    <TableHead className="text-right">28 Day Avg.</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {inventoryItemsConfig.map((item) => {
                    if (item.isHeader) {
                        return (
                            <TableRow key={item.label} className="bg-gray-50 dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                                <TableCell colSpan={4} className="font-semibold text-gray-700 dark:text-gray-200 py-2 px-4">{item.label}</TableCell>
                            </TableRow>
                        );
                    }

                    // Determine current value
                    let currentValue: any;
                    if (item.sourceKey) {
                        currentValue = jlsInventoryData?.[item.sourceKey as keyof JlsInventoryData];
                    } else if (item.valueKey) {
                        currentValue = product[item.valueKey];
                    }

                    let displayValue: string | React.ReactNode;
                    if (item.format === 'daysSincePausedLogic') {
                        displayValue = getDaysSincePausedDisplay();
                    } else {
                        displayValue = formatDisplay(currentValue, item.format);
                    }

                    const oldValue = item.oldValueKey ? oldValuesMap?.[item.oldValueKey] : undefined;
                    const formattedOldValue = formatDisplay(oldValue, item.format);
                    // Compare formatted values for Old Value display logic
                    const displayOldValue = formattedOldValue !== '-' && String(displayValue) !== formattedOldValue;

                    // Get 28 Day Avg value
                    const avgValue = item.avgKey ? averageInventoryData?.[item.avgKey as keyof AverageInventoryData] : undefined;
                    const displayAvgValue = formatDisplay(avgValue, item.format); // Use same format as main value

                    // Determine if the cell should be empty instead of showing '-'
                    const showEmptyOldValue = item.noOldValue;
                    const showEmptyAvgValue = !item.avgKey;

                    return (
                        <TableRow key={item.label} className={cn(
                            // Add bottom border before specific rows for line break effect
                            {'border-b border-gray-200 dark:border-gray-700': ['Days of Cover', 'DOC Threshold to Pause Campaigns', 'Days Since Paused from Low Inventory'].includes(item.label)}
                        )}>
                            <TableCell className="font-medium py-2 px-4">{item.label}</TableCell>
                            <TableCell className={cn("text-right py-1 px-2", // Apply reduced padding here too
                                // Conditional Formatting for Value column - Text Color
                                !isLoading && (
                                    (item.label === 'DSR' && displayValue !== '-') ? 'text-green-700 dark:text-green-400' :
                                    (item.label === 'Days of Cover' && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! < 15) ? 'text-red-600 dark:text-red-400' :
                                    (item.label === 'Days of Cover' && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! < 60) ? 'text-orange-500 dark:text-orange-400' :
                                    (item.label === 'Allow Automatic Pausing from Low Inventory' && displayValue === 'Yes') ? 'text-green-700 dark:text-green-400' :
                                    (item.label === 'Allow Automatic Pausing from Low Inventory' && displayValue === 'No') ? 'text-yellow-500 dark:text-yellow-400' : ''
                                )
                            )}>
                                {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                                    <div className="flex items-center justify-end space-x-1">
                                        {/* Conditional Icon Logic */}
                                        {!isLoading && item.label === 'DSR' && displayValue !== '-' && <CheckCircle size={16} />}
                                        {!isLoading && item.label === 'Days of Cover' && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! < 15 && <AlertCircle size={16} />}
                                        {!isLoading && item.label === 'Days of Cover' && parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! >= 15 && parseNumericValue(currentValue)! < 60 && <AlertCircle size={16} />}
                                        {!isLoading && item.label === 'Allow Automatic Pausing from Low Inventory' && displayValue === 'Yes' && <CheckCircle size={16} />}
                                        <span>{displayValue}</span>
                                    </div>
                                )}
                            </TableCell>
                            <TableCell className="text-right py-1 px-2"> {/* Apply reduced padding */}
                                {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                                    showEmptyOldValue ? <span></span> : ( // Render empty span if no old value expected
                                        displayOldValue ? (
                                            <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100">
                                                {formattedOldValue}
                                            </span>
                                        ) : (
                                            <span>-</span>
                                        )
                                    )
                                )}
                            </TableCell>
                            <TableCell className="text-right py-1 px-2"> {/* Apply reduced padding */}
                                {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                                    showEmptyAvgValue ? <span></span> : displayAvgValue // Render empty span if no avg value expected
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default InventoryDashboard;